import { match } from '@/utils/pattern-match-util'
import { dateFormatters } from '@/utils/date-util'
import {
  type BookkeepingCycle,
  type BookkeepingCycleStatus,
  getBookkeepingCycleCompletedAt,
  getBookkeepingCycleWorkItemsCompletion,
} from './BookkeepingProgressionInfo'

export function formatBookkeepingCycleStatus(status: BookkeepingCycleStatus) {
  return match(status.type, {
    InProgress: 'In progress',
    Completed: 'Completed',
    Canceled: 'Canceled',
    Unknown: 'Unspecified cycle status',
  })
}

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})

const defaultDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})

/**
 * Formats the date with its time, e.g. "January 5, 2025 3:00 PM".
 *
 * @param date
 */
export function formatToLongDateWithTime(date: Date): string {
  return (
    dateFormatters.dateLong.format(date) +
    ' ' +
    dateFormatters.timeDefault.format(date)
  )
}

/**
 * Formats the date, e.g. "Jan 5, 2025".
 *
 * @param date
 */
export function formatShortDate(date: Date): string {
  return defaultDateFormatter.format(date)
}

export function formatCycleToShortDate(cycle: BookkeepingCycle) {
  return `${shortDateFormatter.format(cycle.startedAt)} - ${defaultDateFormatter.format(getBookkeepingCycleCompletedAt(cycle) ?? undefined)}`
}

export function formatCycleCompletionDate(cycle: BookkeepingCycle) {
  return `${dateFormatters.dateNumeric.format(cycle.startedAt)} - ${dateFormatters.dateNumeric.format(getBookkeepingCycleCompletedAt(cycle) ?? undefined)}`
}

export function formatCycleCompletedItems(cycle: BookkeepingCycle) {
  const completedItems = getBookkeepingCycleWorkItemsCompletion(cycle).filter(
    (ci) => ci.completedCount > 0,
  )

  if (!completedItems.length) {
    return 'No items were completed in this cycle.'
  }

  return completedItems
    .map((ci) => `${ci.name} x ${ci.completedCount}`)
    .join(' | ')
}
